import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import MarketMap3D from "@/components/premium/MarketMap3D";
import { flags } from "@/config/featureFlags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MarketsPage — standalone "Best of Best Territory Map" page.
 *
 * If real market-claim data becomes available in the future, pass it via
 * `markets` to the MarketMap3D component. Until then, the map auto-generates
 * a preview from the live category list so the page is never empty.
 */
export default function MarketsPage() {
  const [categories, setCategories] = useState([]);
  const [claimed, setClaimed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catsRes, boBRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios
            .get(`${API}/businesses/paginated`, { params: { is_best_of_best: true, per_page: 60 } })
            .catch(() => ({ data: { businesses: [] } })),
        ]);
        if (cancelled) return;
        setCategories(catsRes.data || []);
        const bobs = boBRes.data?.businesses || boBRes.data?.items || [];
        setClaimed(
          bobs.map((b) => ({
            city: b.city,
            state: b.state,
            category: b.category_name,
            status: "claimed",
            business_name: b.name,
            business_slug: b.slug,
          })),
        );
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen text-white" data-testid="markets-page">
      <Helmet>
        <title>Best of Best Territory Map — LocalBest.ai</title>
        <meta
          name="description"
          content="See which markets and categories are claimed, sponsored, or still open. Reserve your Best of Best category before someone else does."
        />
      </Helmet>

      <section className="relative overflow-hidden py-14 sm:py-20 border-b border-white/5"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(245,158,11,0.18), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(0,229,255,0.14), transparent 55%), linear-gradient(180deg, #0B1220 0%, #050915 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#FBBF24] text-xs font-semibold mb-3">
            Live Territory Map
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Outfit'] leading-tight">
            The <span className="bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#FDE68A] bg-clip-text text-transparent">Best of Best</span> Territory Map
          </h1>
          <p className="text-[#94A3B8] max-w-2xl mx-auto mt-4">
            One Best of Best per category, per market. See what&apos;s claimed, what&apos;s still open, and lock in your spot before someone in your zip code does.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="markets-page-body">
        {!flags.ENABLE_3D_MARKET_MAP ? (
          <p className="text-center text-[#94A3B8] py-16">Market map is temporarily disabled.</p>
        ) : loading ? (
          <p className="text-center text-[#94A3B8] py-16">Loading live territory data…</p>
        ) : (
          <MarketMap3D markets={claimed} categories={categories} showFilters={true} />
        )}
      </section>
    </div>
  );
}
